import { Fragment, type ReactNode, type RefObject } from "react";
import * as Popover from "@radix-ui/react-popover";
import { keyframes, styled } from "@pigment-css/react";
import { toVirtualRef } from "@acto/taskpane/field-sales-2/utils/toVirtualRef";
import { CheckMark2Small } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";

const ANCHOR_GAP_PX = 16;

const popoverIn = keyframes({
  from: { opacity: 0, transform: "scale(0.96)" },
  to: { opacity: 1, transform: "scale(1)" },
});

const popoverOut = keyframes({
  from: { opacity: 1, transform: "scale(1)" },
  to: { opacity: 0, transform: "scale(0.96)" },
});

const PopoverContent = styled(Popover.Content)(({ theme }) => ({
  zIndex: 1000,
  width: "16.25rem",
  padding: `${theme.fieldSalesTwo.spacing[10]} ${theme.fieldSalesTwo.spacing[8]} ${theme.fieldSalesTwo.spacing[8]}`,
  borderRadius: theme.fieldSalesTwo.radius[32],
  background: theme.fieldSalesTwo.colors.surface.glassStrong,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  display: "flex",
  flexDirection: "column",
  transformOrigin: "top right",
  outline: "none",
  fontSize: theme.fieldSalesTwo.font.size.base,
  // Radix reflects the resolved alignment as a data attribute.
  '&[data-align="start"]': {
    transformOrigin: "top left",
  },
  // Radix delays unmount while a CSS animation on data-state="closed" is
  // running. Use keyframes (not transitions) so the exit animation actually
  // plays before the element is removed from the DOM.
  '&[data-state="open"]': {
    animation: `${popoverIn} 150ms cubic-bezier(0.22, 1, 0.36, 1)`,
  },
  '&[data-state="closed"]': {
    animation: `${popoverOut} 120ms ease`,
    pointerEvents: "none",
  },
}));

const Separator = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  height: theme.fieldSalesTwo.spacing[20],
  padding: `0 ${theme.fieldSalesTwo.spacing[12]}`,
  "&::before": {
    content: '""',
    display: "block",
    width: "100%",
    height: "1px",
    background: theme.fieldSalesTwo.colors.neutral[200],
  },
}));

const Item = styled("button")(({ theme }) => ({
  width: "100%",
  height: theme.fieldSalesTwo.spacing[44],
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  padding: `0 ${theme.fieldSalesTwo.spacing[8]}`,
  margin: 0,
  border: "none",
  background: "transparent",
  borderRadius: theme.fieldSalesTwo.radius[8],
  color: theme.fieldSalesTwo.colors.neutral[900],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.tight,
  cursor: "pointer",
  textAlign: "left",
  transition: "background-color 120ms ease",
  "&:hover": {
    backgroundColor: theme.fieldSalesTwo.colors.neutral[200],
  },
  "&:focus-visible": {
    outline: `${theme.strokeWidth.regular} solid ${theme.fieldSalesTwo.colors.neutral[700]}`,
    outlineOffset: `-${theme.strokeWidth.regular}`,
  },
}));

const IconSlot = styled("span")(({ theme }) => ({
  width: theme.fieldSalesTwo.spacing[24],
  height: theme.fieldSalesTwo.spacing[24],
  flex: "0 0 auto",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const Label = styled("span")({
  flex: "1 1 0",
  minWidth: 0,
  textAlign: "left",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const CheckSlot = styled("span")(({ theme }) => ({
  width: theme.fieldSalesTwo.spacing[24],
  height: theme.fieldSalesTwo.spacing[24],
  flex: "0 0 auto",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.fieldSalesTwo.colors.accent.selection,
  "& svg": {
    height: "16px",
    width: "16px",
  },
}));

export interface FilterPopoverItem {
  key: string;
  label: string;
  icon?: ReactNode;
}

export interface FilterPopoverGroup {
  key: string;
  items: FilterPopoverItem[];
}

interface FilterPopoverProps {
  anchorRef: RefObject<HTMLElement | null>;
  isOpen: boolean;
  onClose: () => void;
  groups: FilterPopoverGroup[];
  selectedKeys: string[];
  onToggle: (key: string) => void;
  ariaLabel?: string;
  /** Which trigger edge the popover aligns to; "end" matches the original TopBar use. */
  align?: "start" | "end";
}

export function FilterPopover({
  anchorRef,
  isOpen,
  onClose,
  groups,
  selectedKeys,
  onToggle,
  ariaLabel,
  align = "end",
}: FilterPopoverProps) {
  const t = useTranslations("fieldSalesTwo.search");
  const resolvedAriaLabel = ariaLabel ?? t("filter");

  const selectedSet = new Set(selectedKeys);

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      modal={false}
    >
      <Popover.Anchor virtualRef={toVirtualRef(anchorRef)} />
      <Popover.Portal>
        <PopoverContent
          side="bottom"
          align={align}
          sideOffset={ANCHOR_GAP_PX}
          role="menu"
          aria-orientation="vertical"
          aria-label={resolvedAriaLabel}
          // Track anchor every frame so the popover follows the trigger when
          // it animates (e.g., TitlePill height changes shift the filter button).
          updatePositionStrategy="always"
          onInteractOutside={(e) => {
            // Let the trigger's own onClick toggle the popover. Without this,
            // Radix closes on pointerdown and the trigger's click then re-opens it.
            const target = e.target as Node | null;
            if (target && anchorRef.current?.contains(target)) {
              e.preventDefault();
            }
          }}
          onOpenAutoFocus={(e) => {
            // Radix focuses Content by default; let the first Item take focus instead.
            e.preventDefault();
            const target = e.currentTarget as HTMLElement | null;
            const first = target?.querySelector<HTMLButtonElement>('[role="menuitemcheckbox"]');
            first?.focus();
          }}
        >
          {groups.map((group, groupIndex) => (
            <Fragment key={group.key}>
              {groupIndex > 0 && <Separator aria-hidden="true" />}
              {group.items.map((item) => {
                const checked = selectedSet.has(item.key);
                return (
                  <Item
                    key={item.key}
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={checked}
                    onClick={() => {
                      onToggle(item.key);
                    }}
                  >
                    <IconSlot aria-hidden="true">{item.icon}</IconSlot>
                    <Label>{item.label}</Label>
                    <CheckSlot aria-hidden="true">{checked ? <CheckMark2Small /> : null}</CheckSlot>
                  </Item>
                );
              })}
            </Fragment>
          ))}
        </PopoverContent>
      </Popover.Portal>
    </Popover.Root>
  );
}
